import { Component, OnDestroy, OnInit } from "@angular/core";
import { MenuItem } from "primeng/api";
import { firstValueFrom, Observable, Subject } from "rxjs";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { TabType, TabViewModel } from "@helix/client";
import { select, Store } from "@ngrx/store";
import { AppState } from "../../../../store/reducers";
import { Router, RouterLink } from "@angular/router";
import {
  filter,
  map,
  startWith,
  takeUntil,
  withLatestFrom,
} from "rxjs/operators";
import {
  GetAssetTree,
  GetTabs,
  HideSideBar,
  ShowSideBar,
  TabSelected,
  ToggleAssetTree,
} from "../../../../store/actions/dashboard.actions";
import { AsyncPipe } from "@angular/common";
import { SelectModule } from "primeng/select";
import { FormsModule } from "@angular/forms";
import { faAngleRight, faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NbRouteTab } from "@nebular/theme";
import { AuthService } from "../../../../auth/services/auth.service";

@Component({
  imports: [
    BreadcrumbModule,
    AsyncPipe,
    SelectModule,
    FormsModule,
    FontAwesomeModule,
    RouterLink,
  ],
  selector: "ngx-nav-breadcrumb",
  templateUrl: "./nav-breadcrumb.component.html",
  styleUrls: ["./nav-breadcrumb.component.scss"],
})
export class NavBreadcrumbComponent implements OnInit, OnDestroy {
  readonly home: MenuItem = { icon: "pi pi-home", routerLink: "/" };

  tabsLoading$: Observable<boolean>;
  tabs$: Observable<TabViewModel[]>;
  actualTabs$: Observable<Tab[]>;
  selectedTab$: Observable<TabViewModel>;

  model$: Observable<MenuItem[]>;
  readonly isDropdownMode = true;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
  ) {}

  ngOnDestroy() {
    this.destroyed$.next();
  }

  async ngOnInit() {
    this.authService
      .isAuthenticated()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isAuthenticated) => {
        console.log({ isAuthenticated });
        if (!isAuthenticated) {
          this.store.dispatch(new HideSideBar());
          return;
        }

        this.store.dispatch(new ShowSideBar());
        this.store.dispatch(new GetTabs());
        this.store.dispatch(new GetAssetTree());
        this.store.dispatch(new ToggleAssetTree({ toggle: true }));
      });

    this.tabsLoading$ = this.store.pipe(
      select((state: AppState) => state.dashboard.tabsLoading),
    );

    this.tabs$ = this.store.pipe(
      select((state: AppState) => state.dashboard.tabs),
      filter((x) => x.length > 0),
    );

    this.selectedTab$ = this.store.select((s) => s.dashboard.selectedTab);

    this.actualTabs$ = this.tabs$.pipe(
      withLatestFrom(this.selectedTab$),
      map(([tabs, currentTab]) =>
        tabs
          .filter((tabViewModel) => TabType[tabViewModel.tabType])
          .map((tabViewModel) => {
            const routedTab: Tab = {
              title: tabViewModel.tabName,
              route: this.getRouterLink(tabViewModel),
              responsive: true,
              tab: tabViewModel,
              tabId: tabViewModel.tabId,
            };
            if (
              this.urlMatchesTabRoute(this.router.url, routedTab) &&
              currentTab?.tabId !== routedTab.tab.tabId
            ) {
              this.tabSelected(routedTab);
            }
            return routedTab;
          }),
      ),
    );

    this.model$ = this.selectedTab$.pipe(
      filter((tab) => !!tab),
      map((tab) => {
        return [
          {
            label: tab.tabName,
            routerLink: this.getRouterLink(tab),
          },
        ] as MenuItem[];
      }),
      startWith([{ label: "Loading...", routerLink: "/" }]),
    );

    // this.router.events
    //   .pipe(
    //     tap(console.log),
    //     filter((e) => e instanceof NavigationEnd),
    //     withLatestFrom(this.actualTabs$, this.selectedTab$),
    //     map(([navigationEvent, tabs, currentTab]) => {
    //       const selectedTab = tabs.find((tab) =>
    //         this.urlMatchesTabRoute(navigationEvent.url, tab)
    //       );
    //       if (selectedTab && currentTab?.tabId !== selectedTab.tab.tabId) {
    //         this.tabSelected(selectedTab);
    //       }
    //     }),
    //     takeUntil(this.destroyed$)
    //   )
    //   .subscribe();

    this.selectedTab$
      .pipe(
        filter((tab) => !!tab),
        withLatestFrom(this.actualTabs$),
        takeUntil(this.destroyed$),
      )
      .subscribe(([tab, tabs]) => {
        const targetRoute = ["pages", "dashboard", this.getRouterLink(tab)];
        console.log(targetRoute);
        this.router.navigate(targetRoute, {
          queryParamsHandling: "merge",
          // preserveFragment: true,
          // relativeTo: null,
        });
        this.selectedTab = tabs.find((t) => t.tabId === tab.tabId);
      });
  }

  private getRouterLink(tab: TabViewModel | null | undefined): string {
    if (!tab) {
      return "/";
    }
    return TabType[tab.tabType]?.toLowerCase() ?? "/";
  }

  private urlMatchesTabRoute(url: string, tab: Tab) {
    const urlGroups = url.split("?")[0].split("/");

    return urlGroups[3] === tab.route.toLowerCase();
  }

  public tabSelected(tab: Tab): void {
    // console.log({ tabSelected: tab });
    if (!tab) {
      return;
    }
    this.store.dispatch(new TabSelected(tab.tab));
  }

  protected onSelectionChanged(event: { value: Tab }) {
    this.tabSelected(event.value);
  }

  protected readonly faHome = faHome;
  protected selectedTab: Tab;
  protected readonly faAngleRight = faAngleRight;
}

class Tab implements NbRouteTab {
  tabId: number;
  title: string;
  route: string;
  responsive: boolean;
  tab?: TabViewModel;
}
